import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, InputGroup, InputGroupText, Row, Col } from 'reactstrap';
import { handleAddModifyOperation, fetchTableData } from '../../../../Api/ApiMethods';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Search } from 'react-feather';

const toLocalISOString = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date provided to toLocalISOString:", date);
            return '';
        }
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const localDate = new Date(dateObj.getTime() - tzOffset);
        return localDate.toISOString().slice(0, 16);
    } catch (error) {
        console.error("Error formatting date in toLocalISOString:", error);
        return '';
    }
};

const PaymentCollectionDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedPaymentCollection, refreshData, customerId, autoAssignCustomer = false, onSaveSuccess, directExpenseAssociationId, accidentFile, callForm }) => {
    const initialPaymentDataRef = useRef({
        paymentCollectionNumber: '',
        paymentCollectionType: '',
        amount: '',
        associatedBank: null,
        date: '',
        paymentMethod: '',
        receiptNumber: '',
        description: '',
        expense: [],
        accidentFile: [],
        customer: [],
        callForm: []
    });

    const [paymentData, setPaymentData] = useState(initialPaymentDataRef.current);
    const [errors, setErrors] = useState({});
    const [banks, setBanks] = useState([]);
    const [expenseSearchNumber, setExpenseSearchNumber] = useState('');
    const [associatedExpenseDisplay, setAssociatedExpenseDisplay] = useState('');
    const [associatedExpenseId, setAssociatedExpenseId] = useState(null);
    const [associatedExpenseAmount, setAssociatedExpenseAmount] = useState(null);
    const [isSearchingExpense, setIsSearchingExpense] = useState(false);

    const bankApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/bank';
    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    useEffect(() => {
        const fetchBanks = async () => {
            if (isOpen && bankApiUrl) {
                try {
                    await fetchTableData({
                        apiEndpoint: bankApiUrl,
                        page: 1,
                        pageSize: 1000,
                        setDataCallback: (apiResponse) => {
                            const items = apiResponse?.data?.items || apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
                            setBanks(items);
                        },
                        setTotalRowsCallback: () => {}
                    });
                } catch (error) {
                    console.error("Bank fetch error:", error);
                    toast.error("Bankalar yüklenirken bir hata oluştu.");
                    setBanks([]);
                }
            }
        };
        fetchBanks();
    }, [isOpen, bankApiUrl]);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedPaymentCollection) {
                const associatedExpense = selectedPaymentCollection.expense;
                console.log("Modify Mode - Received associated expense object:", associatedExpense);

                if (associatedExpense && typeof associatedExpense === 'object' && associatedExpense.id) {
                    console.log("Using expense details directly from selectedPaymentCollection");
                    setAssociatedExpenseId(associatedExpense.id);
                    setAssociatedExpenseDisplay(associatedExpense.expenseNumber || `ID: ${associatedExpense.id}`);
                    setAssociatedExpenseAmount(associatedExpense.amount ?? null);
                    setExpenseSearchNumber(associatedExpense.expenseNumber || '');
                } else {
                    console.log("Modify Mode - No valid associated expense object found, resetting fields.");
                    setAssociatedExpenseDisplay('');
                    setAssociatedExpenseId(null);
                    setAssociatedExpenseAmount(null);
                    setExpenseSearchNumber('');
                }
            } else if (mode === 'add') {
                if (directExpenseAssociationId) {
                    console.log(`Directly associating with expense ID: ${directExpenseAssociationId}`);
                    setAssociatedExpenseId(directExpenseAssociationId);
                    setExpenseSearchNumber('');
                    setAssociatedExpenseDisplay('');
                    setAssociatedExpenseAmount(null);
                } else {
                    setExpenseSearchNumber('');
                    setAssociatedExpenseDisplay('');
                    setAssociatedExpenseId(null);
                    setAssociatedExpenseAmount(null);
                }
            }
        }
    }, [isOpen, mode, selectedPaymentCollection, directExpenseAssociationId]);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedPaymentCollection) {
                const formattedDate = toLocalISOString(selectedPaymentCollection.date || new Date());
                const bankId = Array.isArray(selectedPaymentCollection.associatedBank) && selectedPaymentCollection.associatedBank.length > 0
                    ? selectedPaymentCollection.associatedBank[0]
                    : selectedPaymentCollection.associatedBank?.id || '';
                const customers = autoAssignCustomer && customerId
                    ? [customerId]
                    : (Array.isArray(selectedPaymentCollection.customer) ? selectedPaymentCollection.customer : []);
                const accidentFiles = Array.isArray(selectedPaymentCollection.accidentFile) ? selectedPaymentCollection.accidentFile : [];
                const callForms = Array.isArray(selectedPaymentCollection.callForm) ? selectedPaymentCollection.callForm : [];

                const expenseIdForState = (selectedPaymentCollection.expense && typeof selectedPaymentCollection.expense === 'object' && selectedPaymentCollection.expense.id)
                    ? [String(selectedPaymentCollection.expense.id)]
                    : [];

                const currentAccidentFiles = Array.isArray(selectedPaymentCollection.accidentFile) 
                    ? selectedPaymentCollection.accidentFile.map(id => String(id)) 
                    : [];
                const currentCallForms = Array.isArray(selectedPaymentCollection.callForm)
                    ? selectedPaymentCollection.callForm.map(id => String(id))
                    : [];

                setPaymentData({
                    ...initialPaymentDataRef.current,
                    ...selectedPaymentCollection,
                    date: formattedDate,
                    associatedBank: bankId,
                    customer: customers.map(id => String(id)),
                    expense: expenseIdForState,
                    accidentFile: currentAccidentFiles,
                    callForm: currentCallForms,
                    amount: String(selectedPaymentCollection.amount || ''),
                    receiptNumber: String(selectedPaymentCollection.receiptNumber || ''),
                    paymentMethod: selectedPaymentCollection.paymentMethod || '',
                });
            } else {
                setPaymentData({
                    ...initialPaymentDataRef.current,
                    date: '',
                    customer: autoAssignCustomer && customerId ? [String(customerId)] : [],
                    expense: directExpenseAssociationId ? [String(directExpenseAssociationId)] : (associatedExpenseId ? [String(associatedExpenseId)] : []),
                    accidentFile: directExpenseAssociationId && accidentFile ? accidentFile.map(id => String(id)) : [],
                    callForm: directExpenseAssociationId && callForm ? callForm.map(id => String(id)) : [],
                    paymentMethod: '',
                });
            }
            setErrors({});
        }
    }, [isOpen, mode, selectedPaymentCollection, customerId, autoAssignCustomer, directExpenseAssociationId, associatedExpenseId, accidentFile, callForm]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        const fieldsToUppercase = ['paymentCollectionType', 'description'];

        if (fieldsToUppercase.includes(name) && typeof processedValue === 'string') {
            processedValue = processedValue.toLocaleUpperCase('tr-TR');
        }
        else if (['amount', 'receiptNumber'].includes(name)) {
            processedValue = value.replace(/[^0-9.,]/g, '');
            if (name === 'receiptNumber') {
                processedValue = processedValue.replace(/[^0-9]/g, '');
            }
        }

        setPaymentData(prev => ({ ...prev, [name]: processedValue }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const validationErrors = {};

        if (!paymentData.paymentCollectionType?.trim()) {
            validationErrors.paymentCollectionType = 'Tahsilat tipi zorunludur.';
        } else if (paymentData.paymentCollectionType.length < 2) {
            validationErrors.paymentCollectionType = 'Tahsilat tipi en az 2 karakter olmalıdır.';
        }

        if (!paymentData.amount?.toString().trim()) {
            validationErrors.amount = 'Tutar zorunludur.';
        } else {
            const numericValue = paymentData.amount.replace(/,/g, '.');
            if (isNaN(Number(numericValue)) || Number(numericValue) <= 0) {
                validationErrors.amount = 'Geçerli pozitif bir tutar giriniz.';
            }
        }

        if (!paymentData.date) {
            validationErrors.date = 'Tarih zorunludur.';
        } else {
            try {
                new Date(paymentData.date);
            } catch (e) {
                validationErrors.date = 'Geçersiz tarih formatı.';
            }
        }

        if (paymentData.receiptNumber && !/^\d+$/.test(paymentData.receiptNumber)) {
            validationErrors.receiptNumber = 'Makbuz numarası sadece rakam içermelidir.';
        }

        if (paymentData.description && paymentData.description.length > 500) {
            validationErrors.description = 'Açıklama en fazla 500 karakter olabilir.';
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleExpenseSearchChange = (e) => {
        setExpenseSearchNumber(e.target.value);
    };

    const handleSearchExpense = async () => {
        if (!expenseSearchNumber.trim()) {
            toast.info("Lütfen aranacak masraf numarasını girin.");
            return;
        }
        setIsSearchingExpense(true);
        setAssociatedExpenseDisplay('');
        setAssociatedExpenseId(null);
        setAssociatedExpenseAmount(null);

        try {
            let foundExpense = null;
            await fetchTableData({
                apiEndpoint: `${expenseApiUrl}?expenseNumber=${expenseSearchNumber.trim()}`,
                page: 1,
                pageSize: 1,
                setDataCallback: (apiResponse) => {
                    console.log("Expense search apiResponse:", apiResponse);

                    let items = [];
                    if (apiResponse && apiResponse.isSuccess && apiResponse.data && Array.isArray(apiResponse.data.items)) {
                        items = apiResponse.data.items;
                    }
                    else if (apiResponse && Array.isArray(apiResponse.items)) {
                         items = apiResponse.items;
                    } else if (Array.isArray(apiResponse)) {
                         items = apiResponse;
                    }

                    console.log("Extracted items:", items);

                    if (items.length > 0) {
                        foundExpense = items[0];
                        console.log("Found expense object:", foundExpense);
                    } else {
                        console.log("No expense items found in the response structure.");
                    }
                },
                setTotalRowsCallback: () => {}
            });

            if (foundExpense && foundExpense.id) {
                setAssociatedExpenseId(foundExpense.id);
                setAssociatedExpenseDisplay(foundExpense.expenseNumber || `ID: ${foundExpense.id}`);
                setAssociatedExpenseAmount(foundExpense.amount ?? null);
                toast.success(`Masraf "${foundExpense.expenseNumber || foundExpense.id}" bulundu ve ilişkilendirildi.`);
            } else {
                toast.warn(`"${expenseSearchNumber}" numaralı masraf bulunamadı veya yanıt formatı hatalı.`);
                setAssociatedExpenseDisplay('Bulunamadı');
                console.warn("foundExpense was null or did not contain an id after search.");
            }
        } catch (error) {
            console.error("Expense search error:", error);
            toast.error("Masraf aranırken bir hata oluştu.");
            setAssociatedExpenseDisplay('Arama Hatası');
        } finally {
            setIsSearchingExpense(false);
        }
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin.');
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) document.getElementById(firstErrorKey)?.focus();
            return;
        }

        const amountToSend = Number(String(paymentData.amount).replace(/,/g, '.')) || 0;
        const receiptNumberToSend = paymentData.receiptNumber ? Number(paymentData.receiptNumber) : null;
        const dateToSend = paymentData.date ? new Date(paymentData.date).toISOString() : null;
        const bankIdToSend = paymentData.associatedBank ? [String(paymentData.associatedBank)] : [];

        const expenseToSend = directExpenseAssociationId 
            ? [String(directExpenseAssociationId)] 
            : (associatedExpenseId ? [String(associatedExpenseId)] : []);
        const paymentMethodToSend = paymentData.paymentMethod === '' ? null : paymentData.paymentMethod;

        const accidentFileToSend = Array.isArray(paymentData.accidentFile) 
            ? paymentData.accidentFile.map(id => String(id)) 
            : [];
        const callFormToSend = Array.isArray(paymentData.callForm)
            ? paymentData.callForm.map(id => String(id))
            : [];

        const payload = {
            paymentCollectionNumber: paymentData.paymentCollectionNumber || null,
            paymentCollectionType: paymentData.paymentCollectionType,
            amount: amountToSend,
            associatedBank: bankIdToSend,
            date: dateToSend,
            paymentMethod: paymentMethodToSend,
            receiptNumber: receiptNumberToSend,
            description: paymentData.description || '',
            expense: expenseToSend,
            accidentFile: accidentFileToSend,
            customer: Array.isArray(paymentData.customer) ? paymentData.customer.map(id => String(id)) : [],
            callForm: callFormToSend,
            ...(mode === 'modify' && selectedPaymentCollection?.id && { id: selectedPaymentCollection.id })
        };

        console.log("Sending Payload:", payload);

        try {
            const savedItem = await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? { id: selectedPaymentCollection?.id } : null,
                mode,
                data: payload,
                refreshDataCallback: refreshData,
                componentName: 'Tahsilat',
            });

            if (mode === 'add' && onSaveSuccess && savedItem) {
                onSaveSuccess(savedItem);
            }
            toggle();
        } catch (error) {
            console.error("Tahsilat kaydetme hatası:", error);
        }
    };

    const resetForm = () => {
        setPaymentData({
            ...initialPaymentDataRef.current,
            date: '',
            customer: autoAssignCustomer && customerId ? [String(customerId)] : [],
            expense: directExpenseAssociationId ? [String(directExpenseAssociationId)] : (associatedExpenseId ? [String(associatedExpenseId)] : []),
            accidentFile: directExpenseAssociationId && accidentFile ? accidentFile.map(id => String(id)) : [],
            callForm: directExpenseAssociationId && callForm ? callForm.map(id => String(id)) : [],
        });
        setErrors({});
        setExpenseSearchNumber('');
        setAssociatedExpenseDisplay('');
        setAssociatedExpenseId(null);
        setAssociatedExpenseAmount(null);
        setIsSearchingExpense(false);
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    }

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Tahsilat Ekle' : 'Tahsilat Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="paymentCollectionNumber">Tahsilat Numarası</Label>
                        <Input
                            type="text"
                            id="paymentCollectionNumber"
                            name="paymentCollectionNumber"
                            value={paymentData.paymentCollectionNumber || ''}
                            readOnly
                            disabled
                            placeholder="Sistem Tarafından Atanır"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="paymentCollectionType">Tahsilat Tipi <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="paymentCollectionType"
                            name="paymentCollectionType"
                            value={paymentData.paymentCollectionType || ''}
                            onChange={handleInputChange}
                            placeholder="Tahsilatın Türünü Girin (Örn: Müşteri Ödemesi)"
                            invalid={!!errors.paymentCollectionType}
                        />
                        <FormFeedback>{errors.paymentCollectionType}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="amount">Tutar <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="amount"
                            name="amount"
                            value={paymentData.amount || ''}
                            onChange={handleInputChange}
                            placeholder="Örn: 500.50"
                            invalid={!!errors.amount}
                        />
                        <FormFeedback>{errors.amount}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="associatedBank">İlgili Banka</Label>
                        <Input
                            type="select"
                            id="associatedBank"
                            name="associatedBank"
                            value={paymentData.associatedBank || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.associatedBank}
                        >
                            <option value="">Banka Seçiniz (Opsiyonel)</option>
                            {banks?.map(bank => (
                                <option key={bank.id} value={String(bank.id)}>
                                    {bank.bankName} {bank.branchName ? `- ${bank.branchName}` : ''} (IBAN: {bank.iban})
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.associatedBank}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="date">Tarih ve Saat <span className="text-danger">*</span></Label>
                        <Input
                            type="datetime-local"
                            id="date"
                            name="date"
                            value={paymentData.date || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.date}
                        />
                        <FormFeedback>{errors.date}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="paymentMethod">Ödeme Yöntemi</Label>
                        <Input
                            type="select"
                            id="paymentMethod"
                            name="paymentMethod"
                            value={paymentData.paymentMethod || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.paymentMethod}
                        >
                            <option value="">Ödeme Yöntemi Seçiniz</option>
                            <option value="NAKİT">NAKİT</option>
                            <option value="KREDİ KARTI">KREDİ KARTI</option>
                            <option value="HAVALE">HAVALE</option>
                            <option value="PTT">PTT</option>
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label for="receiptNumber">Makbuz Numarası</Label>
                        <Input
                            type="text"
                            id="receiptNumber"
                            name="receiptNumber"
                            value={paymentData.receiptNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Makbuz Numarası (Varsa)"
                            invalid={!!errors.receiptNumber}
                        />
                        <FormFeedback>{errors.receiptNumber}</FormFeedback>
                    </FormGroup>

                    {/* Expense Search UI: REMOVED as per latest request for "Tahsilat Ekle" from PaymentCollections.jsx */}
                    {/* This UI was previously conditional on `mode === 'add' && !directExpenseAssociationId` */}
                    {/* Now, no expense linking UI is shown for mode === 'add' when opened from PaymentCollections.jsx */}


                    {/* Display for already associated expense in 'modify' mode */}
                    {/* This also covers the case where directExpenseAssociationId might be present (though it's for 'add' mode primarily) */}
                    {/* and an expense is linked, OR when simply modifying a payment collection that has an expense. */}
                    {(mode === 'modify' || directExpenseAssociationId) && associatedExpenseDisplay && (
                         <FormGroup>
                            <Label>İlişkili Masraf</Label>
                            <div className="p-2 border rounded" style={{ backgroundColor: '#e9ecef', fontSize: '0.875rem' }}>
                                <div>Masraf Numarası: <strong>{associatedExpenseDisplay}</strong></div>
                                {associatedExpenseAmount !== null && (
                                    <div>Tutar: <strong>{formatCurrency(associatedExpenseAmount)}</strong></div>
                                )}
                            </div>
                        </FormGroup>
                    )}
                    {/* If directExpenseAssociationId is present and no associatedExpenseDisplay yet (e.g. new PC from Expense), show a placeholder or nothing */}
                    {mode === 'add' && directExpenseAssociationId && !associatedExpenseDisplay && (
                        <FormGroup>
                            <Label>İlişkili Masraf</Label>
                            <div className="p-2 border rounded" style={{ backgroundColor: '#e9ecef', fontSize: '0.875rem' }}>
                                Bu tahsilat bir masrafa bağlanacaktır.
                            </div>
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="description">Açıklama</Label>
                        <Input
                            type="textarea"
                            id="description"
                            name="description"
                            value={paymentData.description || ''}
                            onChange={handleInputChange}
                            placeholder="Tahsilatla İlgili Ek Açıklamalar (Opsiyonel)"
                            rows="3"
                            invalid={!!errors.description}
                        />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSave}>
                    {mode === 'add' ? 'Ekle' : 'Güncelle'}
                </Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

PaymentCollectionDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedPaymentCollection: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
    customerId: PropTypes.string,
    autoAssignCustomer: PropTypes.bool,
    onSaveSuccess: PropTypes.func,
    directExpenseAssociationId: PropTypes.string,
    accidentFile: PropTypes.arrayOf(PropTypes.string),
    callForm: PropTypes.arrayOf(PropTypes.string)
};

PaymentCollectionDataModal.defaultProps = {
    onSaveSuccess: null,
    directExpenseAssociationId: null,
    accidentFile: [],
    callForm: []
};

export default PaymentCollectionDataModal;
